import React, { useEffect, Suspense } from 'react';

import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ContactProvider } from './context/ContactContext';
import { LocaleProvider } from './context/LocaleContext';
import ContactModal from './context/ContactModal';
import CookieConsent from './components/CookieConsent/CookieConsent';
import routeMetadata from './data/routeMetadata.json';
import { resolvedComponents } from './routePreload';

// Rend le composant déjà résolu de façon synchrone par le préchargement dans
// index.js — sans aucune <Suspense> autour, pour la route qui vient d'être
// hydratée. La simple présence d'une <Suspense> dans l'arbre (même résolue) fait
// échouer l'hydration ici : le HTML pré-rendu (capturé via Puppeteer, pas du vrai
// SSR streaming) ne contient pas les marqueurs qu'attend React pour reconnaître
// une frontière Suspense déjà résolue.
// Pour toute autre route (navigation client normale, jamais hydratée), on garde
// <Suspense>+lazy comme avant — aucun souci là, ce n'est plus de l'hydration.
function RouteElement({ pattern, Component }) {
  const Resolved = resolvedComponents[pattern];
  if (Resolved) {
    return <Resolved />;
  }
  return (
    <Suspense fallback={null}>
      <Component />
    </Suspense>
  );
}

const HomePage       = React.lazy(() => import('./pages/HomePage'));
const ClientsPage    = React.lazy(() => import('./pages/ClientsPage/ClientsPage'));
const CaseStudyPage  = React.lazy(() => import('./pages/CaseStudyPage/CaseStudyPage'));
const AgentsPage     = React.lazy(() => import('./pages/AgentsPage/AgentsPage'));
const WorkflowsPage  = React.lazy(() => import('./pages/WorkflowsPage/WorkflowsPage'));
const DocumentsAIPage = React.lazy(() => import('./pages/DocumentsAIPage/DocumentsAIPage'));
const ModelsPage     = React.lazy(() => import('./pages/ModelsPage/ModelsPage'));
const ContactCenterPage = React.lazy(() => import('./pages/ContactCenterPage/ContactCenterPage'));
const AboutPage      = React.lazy(() => import('./pages/AboutPage/AboutPage'));
const JobsPage       = React.lazy(() => import('./pages/JobsPage/JobsPage'));
const PartnersPage   = React.lazy(() => import('./pages/PartnersPage/PartnersPage'));
const PartnerDetailPage = React.lazy(() => import('./pages/PartnerDetailPage/PartnerDetailPage'));
const BlogPage       = React.lazy(() => import('./pages/BlogPage/BlogPage'));
const BlogPostPage   = React.lazy(() => import('./pages/BlogPostPage/BlogPostPage'));
const FinancialServicesPage = React.lazy(() => import('./pages/FinancialServicesPage/FinancialServicesPage'));
const PublicGovernmentPage = React.lazy(() => import('./pages/PublicGovernmentPage/PublicGovernmentPage'));
const SmartlyFinanceCenter = React.lazy(() => import('./pages/SmartlyFinanceCenter/SmartlyFinanceCenter'));
const LoginGatewayPage = React.lazy(() => import('./pages/LoginGatewayPage/LoginGatewayPage'));
const DocumentationGatewayPage = React.lazy(() => import('./pages/DocumentationGatewayPage/DocumentationGatewayPage'));
const LegalDocsPage = React.lazy(() => import('./pages/LegalDocsPage/LegalDocsPage'));
const LegalDocumentPage = React.lazy(() => import('./pages/LegalDocsPage/LegalDocumentPage'));
const CodeOfConductPage = React.lazy(() => import('./pages/CodeOfConductPage/CodeOfConductPage'));
const AirTahitiDemoPage = React.lazy(() => import('./pages/AirTahitiDemoPage/AirTahitiDemoPage'));
const HundredPratiquePage = React.lazy(() => import('./pages/HundredPratiquePage/HundredPratiquePage'));
const HundredPratiqueUtilisateursMetierPage = React.lazy(() => import('./pages/HundredPratiqueUtilisateursMetierPage/HundredPratiqueUtilisateursMetierPage'));
const HundredPratiqueEquipeProjetPage = React.lazy(() => import('./pages/HundredPratiqueEquipeProjetPage/HundredPratiqueEquipeProjetPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage/NotFoundPage'));

function normalizePath(pathname = '/') {
  if (!pathname || pathname === '/') {
    return '/';
  }

  return pathname.endsWith('/') ? pathname.slice(0, -1) || '/' : pathname;
}

function toAbsoluteUrl(pathnameOrUrl) {
  if (!pathnameOrUrl) {
    return routeMetadata.siteUrl;
  }

  if (/^https?:\/\//i.test(pathnameOrUrl)) {
    return pathnameOrUrl;
  }

  const normalizedPath = pathnameOrUrl.startsWith('/') ? pathnameOrUrl : `/${pathnameOrUrl}`;

  return `${routeMetadata.siteUrl}${normalizedPath}`;
}

const metadataByPath = new Map(
  routeMetadata.routes.map((route) => [normalizePath(route.path), route])
);

function upsertHeadTag(selector, tagName, attributes) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement(tagName);
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function RouteMetadataManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    const normalizedPath = normalizePath(pathname);
    const metadata = {
      ...routeMetadata.default,
      ...(metadataByPath.get(normalizedPath) || {}),
    };
    const canonicalPath = normalizePath(metadata.canonicalPath || normalizedPath);
    const canonicalUrl = toAbsoluteUrl(canonicalPath);
    const imageUrl = toAbsoluteUrl(metadata.image || routeMetadata.default.image);

    document.documentElement.lang = metadata.lang || routeMetadata.default.lang || 'fr';
    document.title = metadata.title || routeMetadata.default.title;

    upsertHeadTag('meta[name="description"]', 'meta', {
      name: 'description',
      content: metadata.description || routeMetadata.default.description,
    });
    upsertHeadTag('meta[property="og:locale"]', 'meta', {
      property: 'og:locale',
      content: metadata.locale || routeMetadata.default.locale,
    });
    upsertHeadTag('meta[property="og:type"]', 'meta', {
      property: 'og:type',
      content: metadata.type || routeMetadata.default.type,
    });
    upsertHeadTag('meta[property="og:site_name"]', 'meta', {
      property: 'og:site_name',
      content: routeMetadata.siteName,
    });
    upsertHeadTag('meta[property="og:title"]', 'meta', {
      property: 'og:title',
      content: metadata.title || routeMetadata.default.title,
    });
    upsertHeadTag('meta[property="og:description"]', 'meta', {
      property: 'og:description',
      content: metadata.description || routeMetadata.default.description,
    });
    upsertHeadTag('meta[property="og:url"]', 'meta', {
      property: 'og:url',
      content: canonicalUrl,
    });
    upsertHeadTag('meta[property="og:image"]', 'meta', {
      property: 'og:image',
      content: imageUrl,
    });
    upsertHeadTag('meta[property="og:image:alt"]', 'meta', {
      property: 'og:image:alt',
      content: metadata.imageAlt || routeMetadata.default.imageAlt,
    });
    upsertHeadTag('meta[name="twitter:card"]', 'meta', {
      name: 'twitter:card',
      content: metadata.twitterCard || routeMetadata.default.twitterCard,
    });
    upsertHeadTag('meta[name="twitter:title"]', 'meta', {
      name: 'twitter:title',
      content: metadata.title || routeMetadata.default.title,
    });
    upsertHeadTag('meta[name="twitter:description"]', 'meta', {
      name: 'twitter:description',
      content: metadata.description || routeMetadata.default.description,
    });
    upsertHeadTag('meta[name="twitter:image"]', 'meta', {
      name: 'twitter:image',
      content: imageUrl,
    });
    upsertHeadTag('link[rel="canonical"]', 'link', {
      rel: 'canonical',
      href: canonicalUrl,
    });
  }, [pathname]);

  return null;
}

// iOS en mode "Faible consommation" (et l'équivalent économiseur de données sur
// Android) bloque l'autoplay de toute vidéo, même muette — aucun moyen HTML/JS de
// contourner ça directement. Mais un vrai geste utilisateur (tap) débloque .play()
// même dans ce cas : au premier tap sur la page, on relance les vidéos hero encore
// en pause.
function AutoplayFallback() {
  useEffect(() => {
    const resumePausedVideos = () => {
      document.querySelectorAll('video[autoplay]').forEach((video) => {
        if (video.paused) {
          video.play().catch(() => {});
        }
      });
    };

    // Pas de { once: true } : l'app est une SPA qui ne remonte jamais entre les
    // pages, donc une nouvelle vidéo hero peut apparaître (autre route) après le
    // premier tap déjà consommé — on veut rattraper l'autoplay à chaque interaction.
    window.addEventListener('touchstart', resumePausedVideos, { passive: true });
    window.addEventListener('click', resumePausedVideos);

    return () => {
      window.removeEventListener('touchstart', resumePausedVideos);
      window.removeEventListener('click', resumePausedVideos);
    };
  }, []);

  return null;
}

function ScrollManager() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      if (hash) {
        const targetId = decodeURIComponent(hash.slice(1));
        const element = document.getElementById(targetId);

        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
      }

      window.scrollTo(0, 0);
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [pathname, hash]);

  return null;
}

export default function App() {
  return (
    <LocaleProvider>
    <ContactProvider>
    <BrowserRouter>
      <RouteMetadataManager />
      <ScrollManager />
      <AutoplayFallback />
      <ContactModal />
      <CookieConsent />
      <Routes>
        <Route path="/"                        element={<RouteElement pattern="/" Component={HomePage} />} />
        <Route path="/etudes-de-cas"           element={<RouteElement pattern="/etudes-de-cas" Component={ClientsPage} />} />
        <Route path="/etudes-de-cas/:slug"     element={<RouteElement pattern="/etudes-de-cas/:slug" Component={CaseStudyPage} />} />
        <Route path="/products/agents"         element={<RouteElement pattern="/products/agents" Component={AgentsPage} />} />
        <Route path="/products/workflows"      element={<RouteElement pattern="/products/workflows" Component={WorkflowsPage} />} />
        <Route path="/products/documents-ai"   element={<RouteElement pattern="/products/documents-ai" Component={DocumentsAIPage} />} />
        <Route path="/products/models"         element={<RouteElement pattern="/products/models" Component={ModelsPage} />} />
        <Route path="/products/contact-center" element={<RouteElement pattern="/products/contact-center" Component={ContactCenterPage} />} />
        <Route path="/solutions/agents"        element={<Navigate to="/products/agents" replace />} />
        <Route path="/solutions/workflows"     element={<Navigate to="/products/workflows" replace />} />
        <Route path="/solutions/documents-ai"  element={<Navigate to="/products/documents-ai" replace />} />
        <Route path="/solutions/models"        element={<Navigate to="/products/models" replace />} />
        <Route path="/solutions/contact-center" element={<Navigate to="/products/contact-center" replace />} />
        <Route path="/solutions/financial-services" element={<RouteElement pattern="/solutions/financial-services" Component={FinancialServicesPage} />} />
        <Route path="/solutions/public-government" element={<RouteElement pattern="/solutions/public-government" Component={PublicGovernmentPage} />} />
        <Route path="/about-us"                element={<RouteElement pattern="/about-us" Component={AboutPage} />} />
        <Route path="/jobs"                    element={<RouteElement pattern="/jobs" Component={JobsPage} />} />
        <Route path="/partners"                element={<RouteElement pattern="/partners" Component={PartnersPage} />} />
         <Route path="/partners/:slug"          element={<RouteElement pattern="/partners/:slug" Component={PartnerDetailPage} />} />
        <Route path="/blog"                    element={<RouteElement pattern="/blog" Component={BlogPage} />} />
        <Route path="/blog/:slug"              element={<RouteElement pattern="/blog/:slug" Component={BlogPostPage} />} />
        <Route path="/legal-docs"              element={<RouteElement pattern="/legal-docs" Component={LegalDocsPage} />} />
        <Route path="/legal-docs/:slug"        element={<RouteElement pattern="/legal-docs/:slug" Component={LegalDocumentPage} />} />
        <Route path="/code-of-conduct"         element={<RouteElement pattern="/code-of-conduct" Component={CodeOfConductPage} />} />
        <Route path="/connexion"               element={<RouteElement pattern="/connexion" Component={LoginGatewayPage} />} />
        <Route path="/login"                   element={<RouteElement pattern="/login" Component={LoginGatewayPage} />} />
        <Route path="/documentation"           element={<RouteElement pattern="/documentation" Component={DocumentationGatewayPage} />} />
        <Route path="/solutions/finance"       element={<RouteElement pattern="/solutions/finance" Component={SmartlyFinanceCenter} />} />
        <Route path="/clients-demos/air-tahiti" element={<RouteElement pattern="/clients-demos/air-tahiti" Component={AirTahitiDemoPage} />} />
        <Route path="/100-pratique"            element={<RouteElement pattern="/100-pratique" Component={HundredPratiquePage} />} />
        <Route path="/100-pratique-utilisateurs-metier" element={<RouteElement pattern="/100-pratique-utilisateurs-metier" Component={HundredPratiqueUtilisateursMetierPage} />} />
        <Route path="/100-pratique-equipe-projet" element={<RouteElement pattern="/100-pratique-equipe-projet" Component={HundredPratiqueEquipeProjetPage} />} />
        {/* Autres pages à ajouter ici */}
        <Route path="*" element={<Suspense fallback={null}><NotFoundPage /></Suspense>} />
      </Routes>
    </BrowserRouter>
    </ContactProvider>
    </LocaleProvider>
  );
}
